import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  UploadSimple, CheckCircle, Trash, FileCsv, FileXls, FilePdf, FileDoc, FileText, Sparkle, ShieldCheck, LockSimple, DownloadSimple, FileArchive, FileJs, Eye, Warning, Bug, Lightbulb, ChartBar, CircleNotch,
} from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { Section, Pill, EmptyState } from "./_shared";
import { UploadReviewModal, PostUploadProgressModal, AIAssistant } from "./_ai";

const ACCEPT = ".csv,.xlsx,.xls,.pdf,.docx,.txt,.md,text/csv,text/plain,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const SUPPORTED_FORMATS = [
  { label: "CSV", icon: FileCsv, color: "#16A34A" },
  { label: "Excel", icon: FileXls, color: "#0020F5" },
  { label: "PDF", icon: FilePdf, color: "#FF3B30" },
  { label: "Word", icon: FileDoc, color: "#2563EB" },
  { label: "Text", icon: FileText, color: "#52525B" },
];

/* Persistent multi-stage status tracker shown directly under the dropzone.
   Survives modal dismissals; updates when each phase completes; lets the user
   navigate away knowing the project is done populating. */
function IngestStatusBanner({ status, onDismiss, elapsed }) {
  if (!status) return null;
  const { stage, filename, importedCounts, postUploadResult, error } = status;

  const stageOrder = ["reading", "review", "importing", "generating", "done"];
  const idx = stageOrder.indexOf(stage);
  const currentIdx = stage === "error" ? Math.max(0, idx) : idx;

  const steps = [
    { id: "reading",    label: "Reading file",              detail: "AI parsing content" },
    { id: "review",     label: "Review & confirm",          detail: "You picked the rows to import" },
    { id: "importing",  label: "Saving to lenses",          detail: "Risks · Issues · Assumptions" },
    { id: "generating", label: "Generating solutions",      detail: "Mitigations + insights + health score" },
    { id: "done",       label: "All lenses populated",      detail: "Your project is ready" },
  ];

  const stateFor = (i) => {
    if (stage === "error" && i > 0) return "pending";
    if (i < currentIdx) return "done";
    if (i === currentIdx) return stage === "done" ? "done" : "active";
    return "pending";
  };

  const Icon = ({ st }) => {
    if (st === "done")   return <CheckCircle size={18} weight="fill" className="text-[#16A34A]" />;
    if (st === "active") return <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-[#0020F5] border-t-transparent animate-spin" />;
    return <span className="inline-block w-[18px] h-[18px] rounded-full border-2 border-zinc-300" />;
  };

  const isDone = stage === "done";
  const isError = stage === "error";

  return (
    <div
      className={`mt-5 border-l-4 bg-white p-4 ${isError ? "border-[#FF3B30]" : isDone ? "border-[#16A34A]" : "border-[#0020F5]"}`}
      data-testid="ingest-status-banner"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-zinc-500">
            {isError ? "Upload error" : isDone ? "Upload complete" : "Upload in progress"}
          </div>
          <div className="font-display font-bold text-base mt-0.5 text-zinc-950 truncate" title={filename}>
            {filename || "—"}
          </div>
        </div>
        {(isDone || isError) && (
          <button
            type="button"
            onClick={onDismiss}
            className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500 hover:text-zinc-900"
            data-testid="ingest-status-dismiss"
          >
            Dismiss
          </button>
        )}
      </div>

      {/* Per-step list */}
      <div className="mt-4 space-y-2.5">
        {steps.map((s, i) => {
          const st = stateFor(i);
          return (
            <div key={s.id} className="flex items-center gap-3" data-testid={`ingest-status-step-${s.id}`}>
              <Icon st={st} />
              <div className="min-w-0 flex-1 flex items-baseline gap-2">
                <span className={`text-sm font-bold ${st === "active" ? "text-zinc-950" : st === "done" ? "text-zinc-700" : "text-zinc-400"}`}>
                  {s.label}
                </span>
                <span className="text-xs text-zinc-500 truncate">{s.detail}</span>
              </div>
              {st === "active" && !isError && (
                <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#0020F5] whitespace-nowrap">
                  {stage === "reading" && elapsed > 0 ? `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}` : "working…"}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Success summary */}
      {isDone && (
        <div className="mt-4 grid grid-cols-3 gap-2 text-center" data-testid="ingest-status-summary">
          <div className="border border-zinc-200 p-3">
            <div className="font-display font-black text-2xl text-zinc-950">{importedCounts?.assumption || 0}</div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-bold">Assumptions</div>
          </div>
          <div className="border border-zinc-200 p-3">
            <div className="font-display font-black text-2xl text-zinc-950">{importedCounts?.risk || 0}</div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-bold">Risks</div>
          </div>
          <div className="border border-zinc-200 p-3">
            <div className="font-display font-black text-2xl text-zinc-950">{importedCounts?.issue || 0}</div>
            <div className="text-[10px] tracking-[0.15em] uppercase text-zinc-500 font-bold">Issues</div>
          </div>
        </div>
      )}
      {isDone && postUploadResult && (
        <div className="mt-3 text-xs text-zinc-600">
          <strong className="text-zinc-900">{postUploadResult.solutions_added || 0}</strong> solutions generated ·
          health score <strong className="text-zinc-900">{postUploadResult.insights?.health_score ?? "—"}/100</strong>
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="mt-3 text-sm text-[#FF3B30] bg-[#FF3B30]/5 border border-[#FF3B30]/30 p-3" data-testid="ingest-status-error">
          {error || "Something went wrong. Try uploading again."}
        </div>
      )}
    </div>
  );
}

/* Export all lens data — CSV ZIP or single JSON. Dropdown menu next to history header. */
function ExportAllMenu({ projectId, disabled }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async (format) => {
    setBusy(true);
    setOpen(false);
    try {
      const resp = await api.get(`/projects/${projectId}/export-all?format=${format}`, {
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([resp.data], {
        type: format === "json" ? "application/json" : "application/zip",
      });
      const ext = format === "json" ? "json" : "zip";
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `arissai-export.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export downloaded · ${ext.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" data-testid="export-all-menu">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || busy}
        className="inline-flex items-center gap-2 h-10 px-4 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5] disabled:opacity-50"
        data-testid="export-all-trigger"
      >
        <DownloadSimple size={14} weight="bold" />
        {busy ? "Exporting…" : "Export all"}
      </button>
      {open && !busy && (
        <div
          className="absolute right-0 mt-2 w-64 bg-white border border-zinc-200 shadow-lg z-20"
          onMouseLeave={() => setOpen(false)}
        >
          <button
            onClick={() => download("csv-zip")}
            className="w-full text-left px-4 py-3 hover:bg-zinc-50 border-b border-zinc-100 flex items-start gap-3"
            data-testid="export-all-csv-zip"
          >
            <FileArchive size={20} weight="duotone" className="text-[#0020F5] shrink-0" />
            <div>
              <div className="font-bold text-sm text-zinc-950">CSV bundle (.zip)</div>
              <div className="text-xs text-zinc-500 mt-0.5">One CSV per lens · Excel-compatible</div>
            </div>
          </button>
          <button
            onClick={() => download("json")}
            className="w-full text-left px-4 py-3 hover:bg-zinc-50 flex items-start gap-3"
            data-testid="export-all-json"
          >
            <FileJs size={20} weight="duotone" className="text-[#16A34A] shrink-0" />
            <div>
              <div className="font-bold text-sm text-zinc-950">Single JSON</div>
              <div className="text-xs text-zinc-500 mt-0.5">For pipelines, APIs, BI tools</div>
            </div>
          </button>
        </div>
      )}
    </div>
  );
}

/* Free-tier inline upgrade banner — appears only when the current user is on Free
   tier (uploads_per_month = 0). Avoids the dead-end where a Free user drops a file
   and gets a 402. */
function FreeTierUpgradePrompt({ projectId }) { // eslint-disable-line no-unused-vars
  const { user } = useAuth();
  if (!user || (user.subscription_tier && user.subscription_tier !== "free")) return null;
  return (
    <div className="mb-6 border border-[#0020F5] bg-[#E5EAFF] p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="free-tier-upgrade-prompt">
      <div className="w-11 h-11 grid place-items-center bg-[#0020F5] text-white shrink-0">
        <LockSimple size={18} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020F5]">// Free tour</div>
        <div className="font-display font-bold text-base mt-0.5 text-zinc-950">Smart Ingest is included from Starter ($9.99/mo)</div>
        <p className="text-sm text-zinc-700 mt-1 leading-relaxed">
          On the free tour you can explore the seeded demo project. Upload your own status reports, risk registers, vendor SOWs, or messy spreadsheets on any paid plan — AI classifies every item into the right lens automatically.
        </p>
      </div>
      <Link
        to="/pricing"
        className="inline-flex items-center gap-2 h-10 px-5 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5] whitespace-nowrap"
        data-testid="upgrade-to-upload"
      >
        Upgrade to upload →
      </Link>
    </div>
  );
}

/* Per-lens completion strip rendered under each imported file.
   Shows 5 segments: Assumption · Risk · Issue · Solution · Insights.
   - Green-filled + count when populated
   - Spinner when post-upload-generate is still running for the latest file
   - Grey dashed when not applicable / pending */
function LensCompletionStrip({ integration, isLatestRunning }) {
  const counts = integration?.counts_by_lens || {};
  const post = integration?.post_upload_status || {};

  // Solutions + Insights only count when this row is the most recent ingest
  // (post-upload-generate operates on the project, not the file). For older
  // files we still surface what was generated at the time via post_upload_status.
  const segments = [
    { id: "assumption", label: "Assumption", icon: Eye,        count: counts.assumption ?? 0, done: (counts.assumption ?? 0) > 0 },
    { id: "risk",       label: "Risk",       icon: Warning,    count: counts.risk ?? 0,       done: (counts.risk ?? 0) > 0 },
    { id: "issue",      label: "Issue",      icon: Bug,        count: counts.issue ?? 0,      done: (counts.issue ?? 0) > 0 },
    {
      id: "solution",
      label: "Solution",
      icon: Lightbulb,
      count: post.solutions_added ?? 0,
      done: (post.solutions_added ?? 0) > 0,
      pending: isLatestRunning && !post.insights_done,
    },
    {
      id: "insights",
      label: "Insights",
      icon: ChartBar,
      count: post.health_score != null ? `${post.health_score}` : "",
      done: !!post.insights_done,
      pending: isLatestRunning && !post.insights_done,
      suffix: post.health_score != null ? "/100" : "",
    },
  ];

  return (
    <div className="mt-3 grid grid-cols-5 gap-1.5" data-testid={`lens-strip-${integration.id}`}>
      {segments.map((s) => {
        const SegIcon = s.icon;
        const state = s.pending ? "pending" : s.done ? "done" : "idle";
        const bg = state === "done" ? "bg-[#16A34A]" : state === "pending" ? "bg-[#0020F5]" : "bg-zinc-100";
        const fg = state === "idle" ? "text-zinc-500" : "text-white";
        const borderColor = state === "done" ? "border-[#16A34A]" : state === "pending" ? "border-[#0020F5]" : "border-zinc-200";
        return (
          <div
            key={s.id}
            className={`border ${borderColor} px-2 py-1.5 flex items-center gap-2 ${bg}`}
            data-testid={`lens-strip-${integration.id}-${s.id}`}
            title={`${s.label}${s.done ? ` · ${s.count}${s.suffix || ""}` : s.pending ? " · generating…" : " · not yet"}`}
          >
            {state === "pending" ? (
              <CircleNotch size={12} weight="bold" className={`${fg} animate-spin shrink-0`} />
            ) : (
              <SegIcon size={12} weight={state === "done" ? "fill" : "duotone"} className={`${fg} shrink-0`} />
            )}
            <div className="min-w-0 flex-1">
              <div className={`text-[9px] tracking-[0.15em] uppercase font-bold ${fg} truncate`}>{s.label}</div>
              <div className={`text-xs font-bold ${fg} font-mono leading-tight`}>
                {state === "pending" ? "…" : state === "done" ? `${s.count}${s.suffix || ""}` : "—"}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}


export default function IntegrationLens({ projectId, onImported }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [postUploadOpen, setPostUploadOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadFileMeta, setUploadFileMeta] = useState(null); // { name, sizeKB, startedAt }
  const [uploadElapsed, setUploadElapsed] = useState(0);      // seconds since upload start
  const [uploadError, setUploadError] = useState(null);       // { detail, retryFile }
  /* uploadStatus drives the persistent multi-stage tracker shown under the dropzone.
     null = idle, otherwise:
     { stage: "reading"|"review"|"importing"|"generating"|"done"|"error",
       filename, importedCounts, postUploadResult, error } */
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileRef = useRef();

  // Tick the elapsed counter every second while previewing so we can age the message.
  useEffect(() => {
    if (!previewing) {
      setUploadElapsed(0);
      return;
    }
    const t = setInterval(() => setUploadElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [previewing]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/integrations`);
      setItems(data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, [projectId]); // eslint-disable-line

  const disconnect = async (id) => {
    try {
      await api.delete(`/projects/${projectId}/integrations/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
    } catch {
      toast.error("Failed");
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setUploadError(null);
    setUploadFileMeta({
      name: file.name,
      sizeKB: Math.max(1, Math.round(file.size / 1024)),
      startedAt: Date.now(),
    });
    setUploadStatus({ stage: "reading", filename: file.name });
    setPreviewing(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(
        `/projects/${projectId}/integrations/preview`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 },
      );
      setPreview(data);
      setUploadStatus({ stage: "review", filename: file.name });
      setUploadError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg;
      if (err?.response?.status === 402) {
        msg = detail || "Monthly AI quota reached. Upgrade to keep using Smart Ingest.";
      } else if (err?.code === "ECONNABORTED") {
        msg = "The file is taking longer than expected to process. Try a smaller file or split it into sections.";
      } else {
        msg = detail || "Could not read this file. Please make sure it has readable text.";
      }
      setUploadError({ detail: msg, retryFile: file });
      setUploadStatus({ stage: "error", filename: file.name, error: msg });
      toast.error(msg);
    } finally {
      setPreviewing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const retryUpload = () => {
    if (uploadError?.retryFile) handleFile(uploadError.retryFile);
  };

  const clearError = () => {
    setUploadError(null);
    setUploadFileMeta(null);
  };

  const onFileInput = (e) => handleFile(e.target.files?.[0]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!dragOver) setDragOver(true);
  };

  const onDragLeave = () => setDragOver(false);

  const handleImport = async (payload) => {
    setImporting(true);
    setUploadStatus((s) => ({ ...(s || {}), stage: "importing" }));
    try {
      const { data } = await api.post(
        `/projects/${projectId}/integrations/import`,
        payload,
        { timeout: 120000 },
      );
      setItems((p) => [data.integration, ...p]);
      const c = data.counts || {};
      toast.success(
        `Imported ${c.assumption || 0} assumptions · ${c.risk || 0} risks · ${c.issue || 0} issues`,
      );
      setPreview(null);
      setUploadFileMeta(null);
      setUploadStatus((s) => ({
        ...(s || {}),
        stage: "generating",
        importedCounts: c,
      }));
      // Tell the parent (ProjectView) so it can refresh sibling lenses
      onImported?.(c);
      // Trigger post-upload auto-generation (Solutions + Insights)
      setPostUploadOpen(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Import failed";
      toast.error(msg);
      setUploadStatus((s) => ({ ...(s || {}), stage: "error", error: msg }));
    } finally {
      setImporting(false);
    }
  };

  const handlePostUploadDone = (result) => {
    setUploadStatus((s) => ({
      ...(s || {}),
      stage: "done",
      postUploadResult: result || null,
    }));
    // Reload to pick up the post_upload_status (solutions/insights) freshly
    // written on the latest integration row.
    load();
    onImported?.();
  };

  const dismissStatus = () => setUploadStatus(null);

  return (
    <div data-testid="integration-lens">
      <FreeTierUpgradePrompt projectId={projectId} />
      <Section eyebrow="Smart ingest" title="Drop any file — AI does the rest">
        <p className="text-sm text-zinc-600 -mt-2 mb-5 max-w-3xl">
          CSV, Excel, PDF, Word document, plain text — any layout. ARISSAI reads the file,
          extracts assumptions, risks and issues, and shows you a review table before saving.
          After import, Solutions and Insights are auto-generated.
        </p>

        <div
          className="mb-4 flex items-center gap-3 border border-[#0020F5]/30 bg-[#E5EAFF] px-4 py-2.5"
          data-testid="ingest-privacy-ribbon"
        >
          <ShieldCheck size={18} weight="duotone" className="text-[#0020F5] shrink-0" />
          <div className="flex-1 text-xs text-zinc-800 leading-snug">
            <strong className="font-bold">We don't train AI on your data — and we don't keep your raw uploads.</strong>
            <span className="text-zinc-600"> Files are processed in-memory; only the items you save to your project are stored. We use enterprise-grade AI APIs under no-training terms.</span>
          </div>
          <Link
            to="/privacy"
            className="text-[11px] font-bold tracking-[0.1em] uppercase text-[#0020F5] hover:text-zinc-950 whitespace-nowrap"
            data-testid="ingest-privacy-link"
          >
            Privacy →
          </Link>
        </div>

        <button
          type="button"
          onClick={() => !previewing && !uploadError && fileRef.current?.click()}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          disabled={previewing || !!uploadError}
          className={`w-full border-2 border-dashed transition-colors p-10 text-center disabled:cursor-default ${
            uploadError
              ? "border-[#FF3B30]/50 bg-[#FF3B30]/5"
              : previewing
              ? "border-[#0020F5] bg-[#0020F5]/5"
              : dragOver
              ? "border-[#0020F5] bg-[#0020F5]/5"
              : "border-zinc-300 hover:border-zinc-950 hover:bg-zinc-50"
          }`}
          data-testid="smart-ingest-dropzone"
        >
          {uploadError ? (
            <div className="flex flex-col items-center" data-testid="smart-ingest-error">
              <div className="w-12 h-12 rounded-full bg-[#FF3B30]/10 grid place-items-center">
                <Trash size={22} weight="duotone" className="text-[#FF3B30]" />
              </div>
              <div className="font-display font-bold text-lg mt-3 text-zinc-950">Upload failed</div>
              <div className="text-sm text-zinc-600 mt-1 max-w-md">{uploadError.detail}</div>
              {uploadFileMeta && (
                <div className="mt-3 text-[11px] font-mono text-zinc-500">{uploadFileMeta.name} · {uploadFileMeta.sizeKB} KB</div>
              )}
              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); retryUpload(); }}
                  className="inline-flex items-center gap-1.5 h-10 px-5 bg-zinc-950 text-white text-xs font-bold tracking-[0.1em] uppercase hover:bg-[#0020F5]"
                  data-testid="smart-ingest-retry"
                >
                  Try again
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); clearError(); }}
                  className="inline-flex items-center gap-1.5 h-10 px-5 border border-zinc-300 text-zinc-700 text-xs font-bold tracking-[0.1em] uppercase hover:bg-zinc-100"
                  data-testid="smart-ingest-clear-error"
                >
                  Choose a different file
                </button>
              </div>
            </div>
          ) : previewing ? (
            <div className="flex flex-col items-center" data-testid="smart-ingest-progress">
              <Sparkle size={36} weight="fill" className="text-[#0020F5] animate-pulse" />
              <div className="font-display font-bold text-lg mt-3">
                {uploadElapsed < 12 && "AI is reading your file…"}
                {uploadElapsed >= 12 && uploadElapsed < 30 && "Classifying items by lens…"}
                {uploadElapsed >= 30 && uploadElapsed < 60 && "Almost there — large or complex file…"}
                {uploadElapsed >= 60 && uploadElapsed < 120 && "Still working — AI is reading carefully, please don't close this tab…"}
                {uploadElapsed >= 120 && "This is taking longer than usual — hang tight or try a smaller file next time"}
              </div>
              {uploadFileMeta && (
                <div className="mt-2 inline-flex items-center gap-2 px-3 h-7 border border-[#0020F5]/30 bg-white text-xs font-mono">
                  <FileText size={12} weight="duotone" className="text-[#0020F5]" />
                  <span className="font-bold text-zinc-950 max-w-[280px] truncate" title={uploadFileMeta.name}>{uploadFileMeta.name}</span>
                  <span className="text-zinc-500">· {uploadFileMeta.sizeKB} KB</span>
                </div>
              )}
              <div className="mt-3 text-xs text-zinc-500 font-mono">
                {String(Math.floor(uploadElapsed / 60)).padStart(2, "0")}:{String(uploadElapsed % 60).padStart(2, "0")} elapsed
              </div>
              {/* indeterminate progress bar */}
              <div className="mt-4 w-full max-w-md h-1 bg-zinc-200 overflow-hidden">
                <div className="h-full w-1/3 bg-[#0020F5] animate-[upload-slide_1.6s_ease-in-out_infinite]" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <UploadSimple size={36} weight="duotone" className="text-zinc-700" />
              <div className="font-display font-bold text-lg mt-3">Drop a file here, or click to browse</div>
              <div className="text-xs text-zinc-500 mt-1">Max 25 MB · One file at a time</div>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                {SUPPORTED_FORMATS.map((f) => {
                  const Icon = f.icon;
                  return (
                    <span key={f.label} className="inline-flex items-center gap-1.5 px-3 h-7 border border-zinc-200 bg-white text-[11px] font-bold tracking-[0.1em] uppercase" data-testid={`format-${f.label.toLowerCase()}`}>
                      <Icon size={14} weight="duotone" style={{ color: f.color }} />
                      {f.label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          onChange={onFileInput}
          className="hidden"
          data-testid="smart-ingest-file-input"
        />

        <IngestStatusBanner status={uploadStatus} onDismiss={dismissStatus} elapsed={uploadElapsed} />

        <div className="mt-4 flex items-start gap-2 text-xs text-zinc-600 leading-relaxed">
          <Sparkle size={14} weight="fill" className="text-[#0020F5] mt-0.5 shrink-0" />
          <p>
            <strong>No format required.</strong> Drop a vendor SOW, steering minutes, exported risk register,
            status report, or a messy spreadsheet — ARISSAI AI figures out what's an assumption, risk or issue
            and lets you review every item before it's saved. Each upload consumes 1 AI call from your monthly quota.
          </p>
        </div>
      </Section>

      <Section
        eyebrow="History"
        title={`Imported sources (${items.length})`}
        action={<ExportAllMenu projectId={projectId} disabled={items.length === 0} />}
      >
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No imports yet"
            hint="Upload a CSV, Excel, or PDF above to populate the lenses."
            testid="empty-integrations"
          />
        ) : (
          <div className="divide-y divide-zinc-200 border border-zinc-200">
            {items.map((it, idx) => (
              <div
                key={it.id}
                className="p-4"
                data-testid={`integration-row-${it.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <CheckCircle size={20} weight="fill" className="text-[#00C7BE]" />
                    <div>
                      <div className="font-display font-bold text-base">
                        {(it.source || "").toUpperCase()}
                        {it.workspace_name ? ` · ${it.workspace_name}` : ""}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5">
                        {it.items_imported} items imported · last sync{" "}
                        {new Date(it.last_sync).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pill tone="teal">{it.status}</Pill>
                    <button
                      onClick={() => disconnect(it.id)}
                      className="inline-flex items-center justify-center h-9 px-3 border border-zinc-300 text-zinc-700 hover:text-[#FF3B30] hover:border-[#FF3B30]"
                      data-testid={`disconnect-${it.id}`}
                      aria-label="Remove import"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                </div>
                <LensCompletionStrip
                  integration={it}
                  isLatestRunning={idx === 0 && postUploadOpen}
                />
              </div>
            ))}
          </div>
        )}
      </Section>

      <UploadReviewModal
        open={!!preview}
        preview={preview}
        onClose={() => setPreview(null)}
        onImport={handleImport}
        busy={importing}
      />

      <PostUploadProgressModal
        projectId={projectId}
        open={postUploadOpen}
        onClose={() => setPostUploadOpen(false)}
        onDone={handlePostUploadDone}
      />

      <AIAssistant projectId={projectId} lens="integration" label="Integration" />
    </div>
  );
}
